import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Inbox,
  Loader2,
  Search,
  Trash2,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

interface Document {
  id: string;
  uploadDate: string;
  document_name: string;
  file_size?: number;
  chunks_count?: number;
  indexed_chunks?: number;
}

interface UploadProgress {
  fileName: string;
  status: 'processing' | 'completed' | 'error';
  message: string;
  chunksProcessed?: number;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_FILE_PATTERN = /\.(pdf|xlsx|xls)$/i;

const AdminDocumentManagement = () => {
  const { t, i18n } = useTranslation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/documents', {
        method: 'POST',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const acceptFiles = (incomingFiles: File[]) => {
    if (incomingFiles.length > MAX_FILES) {
      toast.error(t('admin.documents.upload.maxFiles', 'Choose up to 5 files'));
    }
    const candidates = incomingFiles.slice(0, MAX_FILES);
    const supported = candidates.filter((file) =>
      SUPPORTED_FILE_PATTERN.test(file.name)
    );
    if (supported.length !== candidates.length) {
      toast.error(
        t('admin.documents.upload.unsupported', 'Only PDF and Excel files are supported')
      );
    }
    const valid = supported.filter((file) => file.size <= MAX_FILE_SIZE);
    if (valid.length !== supported.length) {
      toast.error(
        t('admin.documents.upload.tooLarge', 'Each file must be 10 MB or smaller')
      );
    }
    setSelectedFiles(valid);
    setUploadProgress([]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      acceptFiles(Array.from(files));
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      acceptFiles(Array.from(files));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error(t('admin.documents.upload.selectRequired', 'Choose files first'));
      return;
    }

    try {
      setUploading(true);
      
      // Initialize progress tracking for each file
      const initialProgress: UploadProgress[] = selectedFiles.map(file => ({
        fileName: file.name,
        status: 'processing',
        message: t('admin.documents.upload.processingFile', 'Uploading and indexing…')
      }));
      setUploadProgress(initialProgress);

      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch('/api/process-documents', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();

      // Update with actual results
      setUploadProgress(prev => prev.map((p, idx) => {
        const doc = result.parsed_documents?.[idx];
        return {
          ...p,
          status: 'completed',
          message: t('admin.documents.upload.indexed', 'Indexed successfully'),
          chunksProcessed: doc?.chunks_count || 0
        };
      }));

      toast.success(`${result.message} - ${result.total_chunks || 0} chunks indexed`);

      // Clear after showing completion
      setTimeout(() => {
        setUploadProgress([]);
        setSelectedFiles([]);
        setUploadDialogOpen(false);
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }, 3000);

      // Refresh documents list
      await fetchDocuments();
    } catch (error) {
      console.error('Error uploading documents:', error);
      setUploadProgress(prev => prev.map(p => ({
        ...p,
        status: 'error',
        message: t('admin.documents.upload.failed', 'Upload failed')
      })));
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (documentName: string) => {
    setDocumentToDelete(documentName);
    setDeleteDialogOpen(true);
  };

  const closeUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setUploadProgress([]);
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      const response = await fetch(`/api/delete-document/${encodeURIComponent(documentToDelete)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      toast.success('Document deleted successfully');
      await fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const filteredDocuments = documents.filter((doc) =>
    doc.document_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString(
        i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
        { day: '2-digit', month: 'short', year: 'numeric' }
      );
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '—';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${kb.toFixed(2)} KB`;
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('admin.documents.title', 'Documents')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'admin.documents.description',
              'Manage the files available to the FONGPT knowledge base.'
            )}
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          {t('admin.documents.upload.button', 'Upload files')}
        </Button>
      </div>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {loading ? (
              <Skeleton className="h-5 w-24" />
            ) : searchQuery ? (
              t('admin.documents.list.filteredCount', {
                filtered: filteredDocuments.length,
                total: documents.length,
                defaultValue: `${filteredDocuments.length} of ${documents.length} documents`
              })
            ) : (
              t('admin.documents.list.count', {
                count: documents.length,
                defaultValue: `${documents.length} documents`
              })
            )}
          </p>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t(
                'admin.documents.list.searchPlaceholder',
                'Search documents…'
              )}
              aria-label={t('admin.documents.list.search', 'Search documents')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-9 pl-9"
            />
          </div>
        </div>

        <div className="overflow-x-auto px-5 pb-2 pt-3">
          {loading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="font-medium">
                {t('admin.documents.list.noDocuments', 'No documents found')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {searchQuery
                  ? t(
                      'admin.documents.list.adjustSearch',
                      'Try a different search term.'
                    )
                  : t(
                      'admin.documents.list.empty',
                      'Upload a file to add it to the knowledge base.'
                    )}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[46%]">
                    {t('admin.documents.list.columns.documentName', 'Document')}
                  </TableHead>
                  <TableHead>
                    {t('admin.documents.list.columns.uploadDate', 'Uploaded')}
                  </TableHead>
                  <TableHead>{t('admin.documents.list.columns.size', 'Size')}</TableHead>
                  <TableHead>{t('admin.documents.list.columns.status', 'Status')}</TableHead>
                  <TableHead className="text-right">
                    {t('admin.documents.list.columns.actions', 'Actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocuments.map((doc) => {
                  const indexed = Boolean(doc.indexed_chunks && doc.indexed_chunks > 0);
                  return (
                    <TableRow key={doc.id}>
                      <TableCell className="py-4 font-medium">
                        <div className="flex min-w-0 items-center gap-3">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="max-w-[520px] truncate" title={doc.document_name}>
                            {doc.document_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-4 text-muted-foreground">
                        {formatDate(doc.uploadDate)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-4 text-muted-foreground">
                        {formatFileSize(doc.file_size)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-4">
                        <span className="inline-flex items-center gap-2 text-sm">
                          <span
                            className={`h-2 w-2 rounded-full ${
                              indexed ? 'bg-[#56c0a9]' : 'bg-muted-foreground/50'
                            }`}
                            aria-hidden="true"
                          />
                          {indexed
                            ? t('admin.documents.list.indexed', {
                                count: doc.indexed_chunks,
                                defaultValue: `Indexed · ${doc.indexed_chunks} chunks`
                              })
                            : t('admin.documents.list.pending', 'Pending')}
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-8 w-8"
                          onClick={() => handleDeleteClick(doc.document_name)}
                          title={t('admin.documents.list.deleteButton', 'Delete')}
                          aria-label={t('admin.documents.list.deleteButton', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          if (open) setUploadDialogOpen(true);
          else closeUploadDialog();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin.documents.upload.title', 'Upload files')}</DialogTitle>
            <DialogDescription>
              {t(
                'admin.documents.upload.description',
                'Add up to 5 PDF or Excel files. Maximum size is 10 MB per file.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragging(false)}
            className={`cursor-pointer rounded-xl border border-dashed p-6 text-center transition-colors ${
              isDragging ? 'border-primary bg-muted/50' : 'border-border bg-muted/20'
            }`}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <Upload className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium">
              {t('admin.documents.upload.drop', 'Drop files here or choose files')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">PDF, XLSX, XLS</p>
            <Input
              id="file-upload"
              type="file"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.xlsx,.xls"
              disabled={uploading}
              className="hidden"
            />
          </div>

          {selectedFiles.length > 0 ? (
            <div className="space-y-1 rounded-lg bg-muted/30 p-2">
              {selectedFiles.map((file) => (
                <div key={`${file.name}-${file.size}`} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {uploadProgress.length > 0 ? (
            <div className="space-y-2">
              {uploadProgress.map((progress) => (
                <div
                  key={progress.fileName}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
                >
                  {progress.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#56c0a9]" />
                  ) : progress.status === 'error' ? (
                    <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                  ) : (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{progress.fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      {progress.message}
                      {progress.chunksProcessed !== undefined
                        ? ` · ${progress.chunksProcessed} ${t('admin.documents.upload.chunks', 'chunks')}`
                        : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeUploadDialog}
              disabled={uploading}
            >
              {t('admin.documents.upload.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
            >
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {uploading
                ? t('admin.documents.upload.processing', 'Processing…')
                : t('admin.documents.upload.uploadSelected', {
                    count: selectedFiles.length,
                    defaultValue: `Upload ${selectedFiles.length || ''}`.trim()
                  })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.documents.deleteDialog.title', 'Confirm Deletion')}</DialogTitle>
            <DialogDescription>
              {t('admin.documents.deleteDialog.description', 'Are you sure you want to delete')} "{documentToDelete}"? {t('admin.documents.deleteDialog.actionWarning', 'This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('admin.documents.deleteDialog.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              {t('admin.documents.deleteDialog.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDocumentManagement;
